import type { RequestError } from 'ovpn-ui-toolkit';

const wait = (delayMs: number) =>
    new Promise(res => {
        setTimeout(() => {
            res(undefined);
        }, delayMs);
    });

type RetryParams = {
    times?: number;
    whenErrorCode?: string;
    delay?: number;
    exponential?: boolean;
    when?: (error: any) => boolean;
};

export async function retryFetch<T extends (...args: unknown[]) => Promise<any>>(
    fetchFn: T,
    { times = 20, whenErrorCode = '', delay = 1000, exponential = false, when }: RetryParams,
): Promise<ReturnType<T>> {
    for (let attempt = 1; attempt <= times; attempt++) {
        try {
            return await fetchFn();
        } catch (err) {
            const isLastAttempt = attempt === times;

            const matchesErrorCode = whenErrorCode
                ? (err as RequestError)?.errorCode === whenErrorCode
                : true;

            const matchesCustomCondition = when ? when(err) : true;

            const shouldRetry = !isLastAttempt && (matchesErrorCode || matchesCustomCondition);

            if (!shouldRetry) {
                throw err;
            }

            const currentDelay = exponential ? (2 ** attempt - 1) * delay : delay;

            await wait(currentDelay);
        }
    }

    // This line should never be reached because we throw on the last failed attempt
    throw new Error('Retry logic fell through unexpectedly.');
}
